import jwt from './lib/jwt';
import JwtManager from './lib/jwtManager';
import unauthenticated from '@/routes/unauthenticated';
import RedirectException from '@/exceptions/redirectException';

/**
 * Auth object which provides an interface with details for authenticated user
 * Also handles all communication with server
 */
export default {
  // Service used for authentication actions
  _service: null,
  // Router object to perform redirect actions in Vue context
  _router: null,
  // JWT manager
  _jwtManager: null,
  // Store to contain details of authenticated user
  _store: null,
  _storeNamespace: '',
  // Callbacks to execute after a refresh has completed
  // Each callback should return a promise
  _refreshCallbacks: [],
  // If we're currently waiting on the result of a refresh call
  _pendingRefresh: false,
  // If there's a refresh request pending, this is the Promise that will resolve when it completes
  _currentRefresh: null,
  /**
   * Hook to install Auth object to Vue context
   * Adds reference to to this instance to Vue prototype
   *
   * @param {Object} Vue Global Vue object
   * @param {Object} options Object specifying service and Vue router to use in authentication flow
   */
  install(Vue, options = {}) {
    if (!options.service) {
      throw new Error('No service name defined for authentication');
    }
    if (!Vue.services[options.service]) {
      throw new Error(`Service ${options.service} does not exist`);
    }
    if (!options.store) {
      throw new Error('No store defined for authentication state');
    }
    if (!options.router) {
      throw new Error('No router defined for authentication');
    }

    Vue.prototype.$auth = Vue.auth = this;

    this._service = Vue.services[options.service];
    this._store = options.store;
    if (options.storeNamespace) {
      this._storeNamespace = options.storeNamespace + '/';
    }
    this._router = options.router;
    this._refreshCallbacks = options.refreshCallbacks || [];
    this._jwtManager = new JwtManager({
      accessKey: 'accessToken',
      refreshKey: 'refreshToken',
      refreshBuffer: 60,
      refreshHandler: (withRefresh) => {
        return this._callRefresh(withRefresh);
      },
      refreshErrorHandler: (err) => {
        return this.login()
          .then(() => {
            throw new RedirectException();
          });
      }
    });
  },
  /**
   * Initializer hook for client authorization context
   * Creates User object in memory
   * Any authentication steps (token refresh or Google OAuth sign in) required are carried out by
   * this method
   *
   * @return {*} Promise to sequence further actions
   *             Promise will throw a RedirectException if a redirect is required to bypass later
   *             steps in promise chain
   */
  init() {
    if (!unauthenticated.includes(window.location.pathname)) {
      // Always refresh access token on page load (#10329)
      return this._jwtManager.doRefresh(this._jwtManager.refreshRequiresRefresh())
        .then(() => this.getUser())
        .then(() => this._jwtManager.start());
    } else {
      return Promise.resolve();
    }
  },
  /**
   * Construct User object from stored accessToken
   * If accessToken not in localStorage or accessToken expired, login actions will be performed
   * first to obtain valid accessToken
   *
   * @return {*} Promise which resolves when user details is determined (after any required login
   * steps are required)
   */
  getUser() {
    let token = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    if (token && refreshToken) {
      token = jwt.parse(token);
      if (token.exp * 1000 < Date.now()) {
        token = null;
      }
    }

    if (!token) {
      return this._jwtManager.doRefresh(this._jwtManager.refreshRequiresRefresh());
    }

    if (!('auth' in token.scopes)) {
      this._clearTokens();
      return Promise.reject(new Error('You are not authorized to access this system'));
    }

    this._setUser(token);

    return this.getGroups();
  },
  /**
   * Get groups managed by current user from server
   *
   * @return {PromiseLike<T> | Promise<T> | *} Promise which resolves when AJAX request completes
   *                                           with details of groups
   */
  getGroups() {
    return this._service.get('/groups/managing')
      .then((response) => this._store.commit(this._storeNamespace + 'groups', response.data.data));
  },
  /**
   * Indicate whether the current user has the specified permission in Authenticator service
   *
   * @param {String} permission  Permission to check for
   * @return {boolean}  Whether user has permission
   */
  hasPermission(permission) {
    const store = this._storeNamespace.split('/')
      .reduce((m, sm) => sm.length ? m ? m[sm] : null : m, this._store.state);
    return store && store.user != null &&
      store.user.scopes.auth.permissions.indexOf(permission) > -1;
  },
  /**
   * Execute login flow
   * Initializes login flow with Authentication service then redirects user-agent to required Google
   * endpoint for SSO sign in
   *
   * @return {PromiseLike<T> | Promise<T> | *} Promise to block logic before authentication
   *                                           executed. This will block indefinitely (until page
   *                                           redirect performed) as this is the required flow for
   *                                           login.
   */
  login() {
    sessionStorage.setItem('redirectTo', window.location.pathname + window.location.search);

    return this._service.get('/oauth/login').then((response) => {
      window.location = response.data.location;

      throw new RedirectException();
    });
  },
  /**
   * Method containing logic to handle callback after successfully authenticating to Google OAuth
   * SSO
   * Transmits response from SSO sign in to authentication service & receives JWT in response
   *
   * @return {PromiseLike<T> | Promise<T> | *} Promise that resolves once login has completed
   */
  completeLogin() {
    return this._service.get('/oauth/complete' + window.location.search).then((response) => {
      localStorage.setItem('accessToken', response.data.data.accessToken);
      localStorage.setItem('refreshToken', response.data.data.refreshToken);

      return this.getUser();
    }).then(() => {
      this._jwtManager.start();
      const redirectTo = sessionStorage.getItem('redirectTo');
      sessionStorage.removeItem('redirectTo');
      if (redirectTo) {
        this._router.push({ path: redirectTo });
      }
    }).catch((e) => {
      const msgPrefix = 'Login failed: ';
      if (e.response && e.response.data && e.response.data.error) {
        throw new Error(msgPrefix + e.response.data.error);
      } else if (e.message) {
        throw new Error(msgPrefix + e.message);
      } else if (e) {
        throw new Error(msgPrefix + e);
      } else {
        throw new Error(msgPrefix + 'Something went wrong');
      }
    });
  },
  /**
   * Perform JWT token refresh
   * Uses stored refreshToken to obtain new accessToken
   * Invokes Google OAuth login if refresh token no longer valid
   *
   * @return {*} Promise which resolves when valid accessToken has been obtained
   */
  refresh() {
    if (!this._pendingRefresh) {
      this._pendingRefresh = true;
      this._currentRefresh = this._jwtManager.doRefresh(this._jwtManager.refreshRequiresRefresh())
        .then(() => this._pendingRefresh = false)
        .then(() => this.getGroups())
        .then(() => Promise.all(
          this._refreshCallbacks.map((cb) => cb())
        )).catch((err) => {
          this._pendingRefresh = false;
          return Promise.reject(err);
        });
    }

    return this._currentRefresh;
  },
  _setUser(user) {
    this._store.commit(this._storeNamespace + 'user', user);
  },
  /**
   * Handler to perform token refresh request
   * Performs API call to token refresh endpoint
   * Called by JwtManager when a token refresh is required
   *
   * @param {Boolean} withRefresh Whether to request a new refresh token as well as access token
   * @return {*|PromiseLike<T>|Promise<T>}
   * @private
   */
  _callRefresh(withRefresh) {
    const rawRefreshToken = localStorage.getItem('refreshToken');
    const reqConfig = {
      preventRefresh: true,
      headers: {
        Authorization: `Bearer ${rawRefreshToken}`
      }
    };

    if (withRefresh) {
      reqConfig.params = {
        withRefresh: true
      };
    }

    return this._service.get('/refresh', reqConfig).then((res) => {
      this._setUser(jwt.parse(res.data.data.accessToken));
      return res.data;
    });
  },
  /**
   * Clear stored JWT credentials
   *
   * @private
   */
  _clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }
};
